/**
 * Diccionario parcial en español para Univer 0.4.2.
 *
 * Univer no publica un locale es-ES en su UMD 0.4.2. Este diccionario cubre
 * las cadenas más visibles (toolbar, menús, diálogos comunes, tipografías).
 * Cualquier clave no traducida aquí cae a la de en-US (merge upstream).
 *
 * La estructura replica la del UMD de en-US: `{ "<section>": { ... } }`.
 *
 * Ampliar este diccionario es "seguro": añadir keys nuevas solo añade
 * traducción; quitar keys simplemente devuelve el fallback inglés.
 */
const dict = {
  // Barra cinta superior (tabs)
  ribbon: {
    start: 'Inicio',
    insert: 'Insertar',
    formulas: 'Fórmulas',
    data: 'Datos',
    view: 'Ver',
    others: 'Otras',
    more: 'Más',
  },

  // Botones genéricos
  button: {
    confirm: 'Aceptar',
    cancel: 'Cancelar',
    close: 'Cerrar',
    ok: 'Aceptar',
    save: 'Guardar',
    copy: 'Copiar',
    cut: 'Cortar',
    paste: 'Pegar',
    update: 'Actualizar',
    delete: 'Eliminar',
    insert: 'Insertar',
    prevPage: 'Página anterior',
    nextPage: 'Página siguiente',
    total: 'Total',
  },

  // Alineación (independiente, Univer la referencia en varios sitios)
  align: {
    left: 'Izquierda',
    center: 'Centro',
    right: 'Derecha',
    top: 'Arriba',
    middle: 'Medio',
    bottom: 'Abajo',
  },

  // Modos de ajuste de texto
  textWrap: {
    overflow: 'Desbordar',
    wrap: 'Ajustar texto',
    clip: 'Recortar',
  },

  // Combinar celdas (top-level)
  merge: {
    all: 'Combinar todo',
    vertical: 'Combinar verticalmente',
    horizontal: 'Combinar horizontalmente',
    cancel: 'Separar celdas',
    overlappingError: 'No se pueden combinar celdas con combinaciones existentes.',
    partiallyError: 'La selección contiene celdas combinadas parcialmente.',
    confirm: {
      title: 'Atención',
      content: 'Solo se conserva el valor superior izquierdo.',
      confirm: 'Aceptar',
      cancel: 'Cancelar',
      waring: 'Atención',
    },
  },

  // Formatos numéricos por defecto del toolbar
  defaultFmt: {
    Automatic: 'Automático',
    Number: 'Número',
    Percent: 'Porcentaje',
    PlainText: 'Texto sin formato',
    Scientific: 'Científico',
    Accounting: 'Contabilidad',
    Thousand: 'Millares',
    Currency: 'Moneda',
    Digit: 'Dígito',
    Date: 'Fecha',
    Time: 'Hora',
    Time24H: 'Hora 24h',
    DateTime: 'Fecha y hora',
    DateTime24H: 'Fecha y hora 24h',
    CustomFormats: 'Formatos personalizados',
  },

  // Formato de texto y celdas (la parte más visible del toolbar)
  toolbar: {
    undo: 'Deshacer',
    redo: 'Rehacer',
    paintFormat: 'Copiar formato',
    currencyFormat: 'Formato de moneda',
    percentageFormat: 'Formato de porcentaje',
    numberDecrease: 'Reducir decimales',
    numberIncrease: 'Añadir decimales',
    moreFormats: 'Más formatos',
    font: 'Fuente',
    fontSize: 'Tamaño de fuente',
    bold: 'Negrita',
    italic: 'Cursiva',
    strikethrough: 'Tachado',
    underline: 'Subrayado',
    subscript: 'Subíndice',
    superscript: 'Superíndice',
    textColor: {
      main: 'Color de texto',
      right: 'Elegir color',
    },
    fillColor: {
      main: 'Color de fondo',
      right: 'Elegir color',
    },
    border: {
      main: 'Bordes',
      right: 'Elegir borde',
    },
    mergeCell: {
      main: 'Combinar celdas',
      all: 'Combinar todo',
      vertical: 'Combinar verticalmente',
      horizontal: 'Combinar horizontalmente',
      cancel: 'Separar celdas',
    },
    horizontalAlignMode: {
      main: 'Alineación horizontal',
      left: 'Izquierda',
      center: 'Centro',
      right: 'Derecha',
    },
    verticalAlignMode: {
      main: 'Alineación vertical',
      top: 'Arriba',
      middle: 'Centro',
      bottom: 'Abajo',
    },
    textWrapMode: {
      main: 'Ajuste de texto',
      overflow: 'Desbordar',
      wrap: 'Ajustar',
      clip: 'Recortar',
    },
    textRotateMode: {
      main: 'Rotar texto',
      none: 'Sin rotación',
      angleUp: 'Inclinar arriba',
      angleDown: 'Inclinar abajo',
      vertical: 'Texto vertical',
      rotationUp: 'Rotar 90° arriba',
      rotationDown: 'Rotar 90° abajo',
    },
    resetColor: 'Quitar color',
    customColor: 'Color personalizado',
    alternatingColors: 'Filas alternadas',
    confirmColor: 'Aceptar',
    cancelColor: 'Cancelar',
    collapse: 'Contraer',
    playList: 'Lista',
    insertImage: 'Insertar imagen',
    insertLink: 'Insertar enlace',
    insertVideo: 'Insertar vídeo',
    clearFormat: 'Borrar formato',
    headerFooter: 'Encabezado y pie',
    comment: 'Comentario',
    pivotTable: 'Tabla dinámica',
    search: 'Buscar',
    searchReplace: 'Buscar y reemplazar',
    exportAs: 'Exportar como',
    screenshot: 'Captura de pantalla',
    toggleGridlines: 'Mostrar/ocultar cuadrícula',
    print: 'Imprimir',
  },

  // Menú contextual (clic derecho) — estas son las entradas más pedidas
  rightClick: {
    copy: 'Copiar',
    copyAs: 'Copiar como',
    cut: 'Cortar',
    paste: 'Pegar',
    pasteSpecial: 'Pegado especial',
    pasteValue: 'Pegar solo valores',
    pasteFormat: 'Pegar solo formato',
    pasteColWidth: 'Pegar ancho de columna',
    pasteBesidesBorder: 'Pegar excepto bordes',
    insert: 'Insertar',
    delete: 'Eliminar',
    insertRow: 'Insertar fila',
    insertRowBefore: 'Insertar fila arriba',
    insertRowsAbove: 'Insertar filas arriba',
    insertRowsAfter: 'Insertar filas debajo',
    insertRowsColumnsNumber: '¿Cuántas filas/columnas?',
    insertColBefore: 'Insertar columna a la izquierda',
    insertColAfter: 'Insertar columna a la derecha',
    insertColsLeft: 'Insertar columnas a la izquierda',
    insertColsRight: 'Insertar columnas a la derecha',
    deleteCell: 'Eliminar celdas',
    deleteSelected: 'Eliminar seleccionadas',
    deleteSelectedRow: 'Eliminar filas seleccionadas',
    deleteSelectedCol: 'Eliminar columnas seleccionadas',
    deleteSelectedCellDialog: 'Eliminar celda',
    moveLeft: 'Desplazar a la izquierda',
    moveUp: 'Desplazar hacia arriba',
    moveRight: 'Desplazar a la derecha',
    moveDown: 'Desplazar hacia abajo',
    hideSelected: 'Ocultar',
    showHide: 'Mostrar',
    rowHeight: 'Alto de fila',
    rowHeightDialog: 'Introduce el alto en píxeles',
    colWidth: 'Ancho de columna',
    colWidthDialog: 'Introduce el ancho en píxeles',
    clearContent: 'Borrar contenido',
    clearFormat: 'Borrar formato',
    clearAll: 'Borrar todo',
    clearSelection: 'Borrar',
    protectRange: 'Proteger filas y columnas',
    protectRangeShort: 'Proteger',
    matrix: 'Matriz',
    sortAsc: 'Ordenar ascendente',
    sortDesc: 'Ordenar descendente',
    freeze: 'Inmovilizar',
    freezeCol: 'Inmovilizar columnas',
    freezeRow: 'Inmovilizar filas',
    cancelFreeze: 'Quitar inmovilización',
    toTopAdd: 'Añadir arriba',
    toBottomAdd: 'Añadir abajo',
    toLeftAdd: 'Añadir a la izquierda',
    toRightAdd: 'Añadir a la derecha',
    insertHyperlink: 'Insertar enlace',
    editHyperlink: 'Editar enlace',
    cancelHyperlink: 'Quitar enlace',
    copyHyperlink: 'Copiar enlace',
  },

  // Panel inferior (pestañas de hojas)
  sheetConfig: {
    delete: 'Eliminar',
    copy: 'Duplicar',
    rename: 'Cambiar nombre',
    changeColor: 'Cambiar color',
    hide: 'Ocultar',
    unhide: 'Mostrar',
    moveLeft: 'Mover a la izquierda',
    moveRight: 'Mover a la derecha',
    addSheet: 'Añadir hoja',
    setColor: 'Color de pestaña',
    cancelColor: 'Sin color',
    tabColor: 'Color de pestaña',
    resetColor: 'Quitar color',
    chooseColor: 'Elegir color',
    cancelText: 'Cancelar',
    chooseText: 'Aceptar',
    tipNameRepeat: 'No se puede repetir el nombre de la hoja. Usa otro.',
    noMoreSheet: 'No se puede eliminar la última hoja.',
    confirmDelete: 'Confirmar eliminación',
    redoDelete: '¿Seguro que quieres eliminar esta hoja?',
    noHide: 'No se puede ocultar la única hoja visible.',
    chartEditNoOpt: 'No se puede realizar esta operación mientras se edita un gráfico.',
    sheetNameErrorTitle: 'Nombre no válido',
    deleteSheet: 'Eliminar hoja',
    deleteSheetContent: 'Esta acción no se puede deshacer. ¿Quieres continuar?',
    addProtectSheet: 'Proteger hoja',
    removeProtectSheet: 'Quitar protección de hoja',
    viewAllProtectArea: 'Ver todas las áreas protegidas',
    changeSheetPermission: 'Cambiar permisos de la hoja',
  },

  // Menú contextual de protección de hoja (aparecían en inglés)
  protectSheet: {
    protectSheet: 'Proteger hoja',
    unprotectSheet: 'Quitar protección',
    changeSheetPermissions: 'Cambiar permisos',
    viewAllProtectRanges: 'Ver rangos protegidos',
  },

  // Atajos de teclado (panel lateral — el top-level 'shortcut-panel' se
  // declara m\u00e1s abajo junto con el resto de traducciones de atajos.)
  shortcutPanel: {
    title: 'Atajos de teclado',
  },

  // Diálogos
  dialog: {
    confirm: 'Aceptar',
    cancel: 'Cancelar',
    close: 'Cerrar',
  },

  // Algunos mensajes comunes
  message: {
    success: 'Operación completada',
    error: 'Se produjo un error',
    warning: 'Advertencia',
  },

  // Formatos numéricos frecuentes
  format: {
    general: 'General',
    text: 'Texto',
    number: 'Número',
    percent: 'Porcentaje',
    scientific: 'Científico',
    accounting: 'Contabilidad',
    currency: 'Moneda',
    date: 'Fecha',
    time: 'Hora',
    datetime: 'Fecha y hora',
    fraction: 'Fracción',
    moreNumber: 'Más formatos numéricos',
    moreDate: 'Más formatos de fecha',
    moreCurrency: 'Más monedas',
    thousands: 'Millares',
  },

  // Pegado especial
  pasteSpecial: {
    title: 'Pegado especial',
    value: 'Valores',
    format: 'Formato',
    formula: 'Fórmulas',
    'col-width': 'Ancho de columna',
    'besides-border': 'Excepto bordes',
  },

  // Estado / loading / empty
  loading: {
    loading: 'Cargando…',
  },
  empty: {
    empty: 'Sin datos',
  },

  // ---------------------------------------------------------------------
  // Univer Docs — claves espec\u00edficas de documentos
  // ---------------------------------------------------------------------
  doc: {
    slogan: 'Iberbox Docs',
    menu: {
      file: 'Archivo',
      edit: 'Editar',
      insert: 'Insertar',
      format: 'Formato',
      tools: 'Herramientas',
      view: 'Ver',
      help: 'Ayuda',
    },
    headerFooter: {
      header: 'Encabezado',
      footer: 'Pie de p\u00e1gina',
      firstPage: 'P\u00e1gina especial para la primera',
      oddEven: 'Diferente en impares y pares',
      margin: 'Margen',
      options: 'Opciones',
    },
    paragraphSetting: {
      title: 'P\u00e1rrafo',
      lineSpacing: 'Espaciado de l\u00edneas',
      indentFirstLine: 'Sangr\u00eda de primera l\u00ednea',
      indentHanging: 'Sangr\u00eda francesa',
      spacingBefore: 'Antes del p\u00e1rrafo',
      spacingAfter: 'Despu\u00e9s del p\u00e1rrafo',
      alignment: 'Alineaci\u00f3n',
      left: 'Izquierda',
      center: 'Centro',
      right: 'Derecha',
      justify: 'Justificado',
    },
    textAlign: {
      left: 'Alinear a la izquierda',
      center: 'Centrar',
      right: 'Alinear a la derecha',
      justified: 'Justificar',
    },
    list: {
      ordered: 'Lista numerada',
      unordered: 'Lista con vi\u00f1etas',
      checklist: 'Lista de comprobaci\u00f3n',
      increaseIndent: 'Aumentar sangr\u00eda',
      decreaseIndent: 'Disminuir sangr\u00eda',
      clearFormat: 'Borrar formato',
    },
    pageSize: {
      A4: 'A4',
      Letter: 'Carta',
      Legal: 'Oficio',
      Tabloid: 'Tabloide',
      custom: 'Personalizado',
    },
    link: {
      title: 'Enlace',
      url: 'URL',
      text: 'Texto',
      insert: 'Insertar enlace',
      edit: 'Editar enlace',
      remove: 'Quitar enlace',
    },
    find: {
      title: 'Buscar',
      replace: 'Reemplazar',
      findInput: 'Buscar',
      replaceInput: 'Reemplazar por',
      caseSensitive: 'Distinguir may\u00fas./min\u00fas.',
      wholeWord: 'Solo palabras completas',
      next: 'Siguiente',
      prev: 'Anterior',
      replaceAll: 'Reemplazar todo',
      noMatches: 'No se encontraron coincidencias',
    },
    selection: {
      documentStart: 'Inicio del documento',
      documentEnd: 'Final del documento',
    },
  },

  // Algunos comandos de texto (Docs) que aparec\u00edan sin traducir
  docUi: {
    'align-left': 'Alinear a la izquierda',
    'align-center': 'Centrar',
    'align-right': 'Alinear a la derecha',
    'align-justify': 'Justificar',
    'list-bullet': 'Lista con vi\u00f1etas',
    'list-ordered': 'Lista numerada',
    'indent-increase': 'Aumentar sangr\u00eda',
    'indent-decrease': 'Disminuir sangr\u00eda',
    'line-spacing': 'Espaciado entre l\u00edneas',
    'break-line': 'Salto de l\u00ednea',
    'page-break': 'Salto de p\u00e1gina',
  },

  // ---------------------------------------------------------------------
  // Claves top-level sueltas que Univer usa como literales en UI
  // ---------------------------------------------------------------------
  'common-edit': 'Atajos de edici\u00f3n comunes',
  'sheet-view': 'Vista de hoja',
  'toggle-shortcut-panel': 'Mostrar/Ocultar panel de atajos',
  'update-status': 'Estado de actualizaci\u00f3n',
  uploadLoading: {
    loading: 'Cargando\u2026',
    error: 'Error al cargar',
  },

  // Barra de estado inferior
  statusbar: {
    sum: 'Suma',
    average: 'Promedio',
    max: 'M\u00e1ximo',
    min: 'M\u00ednimo',
    count: 'N\u00famero',
    countA: 'Contar',
    countNumber: 'Contar n\u00fameros',
  },

  // ---------------------------------------------------------------------
  // Atajos de teclado (panel lateral completo)
  // ---------------------------------------------------------------------
  shortcut: {
    undo: 'Deshacer',
    redo: 'Rehacer',
    cut: 'Cortar',
    copy: 'Copiar',
    paste: 'Pegar',
    'shortcut-panel': 'Mostrar/ocultar panel de atajos',
    sheet: {
      'select-up-cell': 'Seleccionar celda superior',
      'select-below-cell': 'Seleccionar celda inferior',
      'select-left-cell': 'Seleccionar celda izquierda',
      'select-right-cell': 'Seleccionar celda derecha',
      'select-up-value-cell': 'Saltar al contenido por encima',
      'select-below-value-cell': 'Saltar al contenido por debajo',
      'select-left-value-cell': 'Saltar al contenido a la izquierda',
      'select-right-value-cell': 'Saltar al contenido a la derecha',
      'select-next-cell': 'Siguiente celda',
      'select-previous-cell': 'Celda anterior',
      'select-all': 'Seleccionar todo',
      'expand-selection-up': 'Ampliar selecci\u00f3n arriba',
      'expand-selection-down': 'Ampliar selecci\u00f3n abajo',
      'expand-selection-left': 'Ampliar selecci\u00f3n a la izquierda',
      'expand-selection-right': 'Ampliar selecci\u00f3n a la derecha',
      'expand-selection-to-up-gap': 'Ampliar selecci\u00f3n hasta la zona superior',
      'expand-selection-to-below-gap': 'Ampliar selecci\u00f3n hasta la zona inferior',
      'expand-selection-to-left-gap': 'Ampliar selecci\u00f3n hasta la zona izquierda',
      'expand-selection-to-right-gap': 'Ampliar selecci\u00f3n hasta la zona derecha',
      'start-editing': 'Iniciar edici\u00f3n (modo editor)',
      'toggle-editing': 'Alternar edici\u00f3n',
      'delete-and-start-editing': 'Borrar y editar',
      'abort-editing': 'Cancelar edici\u00f3n',
      'break-line': 'Salto de l\u00ednea en celda',
      'set-bold': 'Alternar negrita',
      'set-italic': 'Alternar cursiva',
      'set-underline': 'Alternar subrayado',
      'set-strike-through': 'Alternar tachado',
      'zen-edit-cancel': 'Cancelar edici\u00f3n Zen',
      'zen-edit-confirm': 'Confirmar edici\u00f3n Zen',
      'zoom-in': 'Acercar zoom',
      'zoom-out': 'Alejar zoom',
      'reset-zoom': 'Restablecer zoom',
      'drawing-delete': 'Eliminar dibujo',
      'drawing-move-up': 'Mover dibujo arriba',
      'drawing-move-down': 'Mover dibujo abajo',
      'drawing-move-left': 'Mover dibujo a la izquierda',
      'drawing-move-right': 'Mover dibujo a la derecha',
    },
  },
  'shortcut-panel': {
    title: 'Atajos de teclado',
  },
  'global-shortcut': {
    title: 'Atajos globales',
    undo: 'Deshacer',
    redo: 'Rehacer',
    'redo-alt': 'Rehacer (alternativo)',
  },

  // ---------------------------------------------------------------------
  // Header & Footer (encabezado y pie de p\u00e1gina de Docs)
  // ---------------------------------------------------------------------
  headerFooter: {
    panel: 'Encabezado y pie de p\u00e1gina',
    header: 'Encabezado',
    footer: 'Pie de p\u00e1gina',
    headerTopMargin: 'Margen superior del encabezado (px)',
    footerBottomMargin: 'Margen inferior del pie (px)',
    firstPageCheckBox: 'P\u00e1gina especial para la primera',
    oddEvenCheckBox: 'Diferente en p\u00e1ginas pares e impares',
    closeHeaderFooter: 'Cerrar encabezado y pie',
    disableText: 'Los ajustes de encabezado y pie est\u00e1n deshabilitados',
  },

  // ---------------------------------------------------------------------
  // Panel de im\u00e1genes (insertar / posicionar / recortar / alinear)
  // ---------------------------------------------------------------------
  'image-panel': {
    null: 'Ning\u00fan objeto seleccionado',
    arrange: {
      title: 'Organizar',
      forward: 'Traer adelante',
      back: 'Enviar al fondo',
      backward: 'Enviar atr\u00e1s',
      front: 'Traer al frente',
    },
    align: {
      title: 'Alinear',
      default: 'Seleccionar tipo de alineaci\u00f3n',
      left: 'Alinear izquierda',
      center: 'Alinear centro',
      right: 'Alinear derecha',
      top: 'Alinear arriba',
      middle: 'Alinear medio',
      bottom: 'Alinear abajo',
      horizon: 'Distribuir horizontalmente',
      vertical: 'Distribuir verticalmente',
    },
    transform: {
      title: 'Transformar',
      x: 'X (px)',
      y: 'Y (px)',
      width: 'Ancho (px)',
      height: 'Alto (px)',
      rotate: 'Rotar (\u00b0)',
      lock: 'Bloquear proporci\u00f3n (%)',
    },
    crop: {
      title: 'Recortar',
      start: 'Iniciar recorte',
      mode: 'Libre',
    },
    group: {
      title: 'Agrupar',
      group: 'Agrupar',
      reGroup: 'Reagrupar',
      unGroup: 'Desagrupar',
    },
  },

  // ---------------------------------------------------------------------
  // Buscar y reemplazar
  // ---------------------------------------------------------------------
  'find-replace': {
    find: 'Buscar',
    replace: 'Reemplazar',
    replaceWith: 'Reemplazar con',
    findAll: 'Buscar todo',
    replaceAll: 'Reemplazar todo',
    next: 'Siguiente',
    previous: 'Anterior',
    close: 'Cerrar',
    noResult: 'Sin resultados',
    matchCase: 'Distinguir may\u00fas./min\u00fas.',
    matchWord: 'Coincidir palabra completa',
    useRegex: 'Expresi\u00f3n regular',
    scope: '\u00c1mbito',
    toolbar: {
      title: 'Buscar y reemplazar',
    },
  },
  'find-replace-shortcuts': {
    'open-find': 'Abrir buscar',
    'open-replace': 'Abrir buscar y reemplazar',
    'find-next': 'Buscar siguiente',
    'find-previous': 'Buscar anterior',
  },
  'sheet-find-replace': {
    'find-scope': '\u00c1mbito de b\u00fasqueda',
    'in-sheet': 'En hoja actual',
    'in-workbook': 'En todo el libro',
    'in-selection': 'En selecci\u00f3n actual',
    replaceNone: 'Sin coincidencias para reemplazar',
    replaceSuccess: 'Se reemplazaron {0} coincidencias',
  },

  // ---------------------------------------------------------------------
  // Ordenar y filtrar (hojas)
  // ---------------------------------------------------------------------
  'sheets-sort': {
    title: 'Ordenar',
    asc: 'Ascendente',
    desc: 'Descendente',
    customSort: 'Ordenaci\u00f3n personalizada',
    header: 'Mi selecci\u00f3n tiene encabezado',
    case: 'Distinguir may\u00fas./min\u00fas.',
    addRule: 'A\u00f1adir nivel',
    sortBy: 'Ordenar por',
    thenBy: 'Luego por',
    column: 'Columna',
    order: 'Orden',
    ok: 'Aceptar',
    cancel: 'Cancelar',
  },
  'sheets-filter': {
    title: 'Filtro',
    enable: 'Activar filtro',
    disable: 'Quitar filtro',
    clearFilter: 'Limpiar filtro',
    byColor: 'Filtrar por color',
    byValue: 'Filtrar por valor',
    byCondition: 'Filtrar por condici\u00f3n',
    searchItems: 'Buscar valores\u2026',
    selectAll: 'Seleccionar todo',
    deselectAll: 'Deseleccionar todo',
    apply: 'Aplicar',
    cancel: 'Cancelar',
  },

  // ---------------------------------------------------------------------
  // Comentarios (hoja y doc)
  // ---------------------------------------------------------------------
  threadCommentUI: {
    panel: 'Comentarios',
    empty: 'Sin comentarios',
    addComment: 'A\u00f1adir comentario',
    placeholder: 'Escribe un comentario\u2026',
    submit: 'Enviar',
    cancel: 'Cancelar',
    reply: 'Responder',
    delete: 'Eliminar',
    edit: 'Editar',
    resolve: 'Resolver',
    reopen: 'Reabrir',
    mentioned: 'mencion\u00f3',
    replyPlaceholder: 'Responder\u2026',
  },
  sheetThreadComment: {
    addComment: 'A\u00f1adir comentario',
    editComment: 'Editar comentario',
    deleteComment: 'Eliminar comentario',
    resolve: 'Resolver hilo',
    reopen: 'Reabrir hilo',
    mention: '@mencionar',
    commentBy: 'Comentario de',
  },

  // ---------------------------------------------------------------------
  // Enlaces (hiperv\u00ednculos)
  // ---------------------------------------------------------------------
  hyperLink: {
    form: {
      label: 'Texto',
      labelPlaceholder: 'Texto a mostrar',
      type: 'Destino',
      typeMenu: {
        link: 'Enlace web',
        range: 'Rango de celdas',
        sheet: 'Hoja',
        definedName: 'Nombre definido',
      },
      addressPlaceholder: 'Introduce la URL',
      ok: 'Aceptar',
      cancel: 'Cancelar',
    },
    menu: {
      insert: 'Insertar enlace',
      edit: 'Editar enlace',
      remove: 'Quitar enlace',
      copy: 'Copiar enlace',
      open: 'Abrir enlace',
    },
    copied: 'Enlace copiado',
    invalid: 'Enlace no v\u00e1lido',
  },

  // ---------------------------------------------------------------------
  // Permisos (compartir / proteger)
  // ---------------------------------------------------------------------
  permission: {
    title: 'Permisos',
    panel: {
      title: 'Permisos del rango',
      name: 'Nombre',
      ranges: 'Rangos',
      scope: '\u00c1mbito',
      editBy: 'Editado por',
      allowEdit: 'Permitir edici\u00f3n',
      onlyMe: 'Solo yo',
      everyone: 'Todos',
      description: 'Descripci\u00f3n',
      placeholder: 'Opcional: describe este permiso',
      protect: 'Proteger',
      unprotect: 'Quitar protecci\u00f3n',
      remove: 'Eliminar',
      confirm: 'Confirmar',
      cancel: 'Cancelar',
    },
    dialog: {
      allowedTitle: 'Acci\u00f3n permitida',
      forbiddenTitle: 'Acci\u00f3n no permitida',
      forbidden: 'No tienes permiso para realizar esta acci\u00f3n',
      confirm: 'Aceptar',
    },
    error: 'No tienes permiso para realizar esta acci\u00f3n',
  },

  // ---------------------------------------------------------------------
  // F\u00f3rmulas (editor de f\u00f3rmula, lista, ayuda contextual)
  // ---------------------------------------------------------------------
  formula: {
    insert: {
      tooltip: 'Funciones',
      insertFunction: 'Insertar funci\u00f3n',
      insertMore: 'M\u00e1s funciones',
      sum: 'SUMA',
      average: 'PROMEDIO',
      count: 'CONTAR',
      max: 'M\u00c1XIMO',
      min: 'M\u00cdNIMO',
    },
    prompt: {
      helpExample: 'Ejemplo',
      helpAbstract: 'Resumen',
    },
    more: {
      tip: 'M\u00e1s funciones',
      ok: 'Aceptar',
      cancel: 'Cancelar',
      search: 'Buscar funci\u00f3n',
      function: 'Funci\u00f3n',
      description: 'Descripci\u00f3n',
      abstract: 'Resumen',
      example: 'Ejemplo',
      parameter: 'Par\u00e1metros',
      done: 'Hecho',
    },
    formulaMore: 'M\u00e1s funciones',
    functionList: {
      all: 'Todas',
      recent: 'Recientes',
      financial: 'Financieras',
      date: 'Fecha y hora',
      math: 'Matem\u00e1ticas y trigonometr\u00eda',
      statistical: 'Estad\u00edsticas',
      lookup: 'B\u00fasqueda y referencia',
      database: 'Base de datos',
      text: 'Texto',
      logical: 'L\u00f3gicas',
      information: 'Informaci\u00f3n',
      engineering: 'Ingenier\u00eda',
      compatibility: 'Compatibilidad',
      cube: 'Cubo',
      web: 'Web',
      array: 'Matriciales',
    },
  },

  // ---------------------------------------------------------------------
  // Rotaci\u00f3n de texto + wrap (ya existen en toolbar, pero Univer referencia
  // sus versiones top-level tambi\u00e9n).
  // ---------------------------------------------------------------------
  textRotate: {
    none: 'Sin rotaci\u00f3n',
    angleUp: 'Inclinar arriba',
    angleDown: 'Inclinar abajo',
    vertical: 'Texto vertical',
    rotationUp: 'Rotar 90\u00b0 arriba',
    rotationDown: 'Rotar 90\u00b0 abajo',
  },

  // ---------------------------------------------------------------------
  // Info / acerca de / auto-completado
  // ---------------------------------------------------------------------
  info: {
    tooltip: {
      undo: 'Deshacer',
      redo: 'Rehacer',
      hover: 'Haz clic para m\u00e1s',
    },
  },
  autoFill: {
    copy: 'Copiar celdas',
    series: 'Rellenar serie',
    formatOnly: 'Solo formato',
    noFormat: 'Sin formato',
    other: 'Otras opciones',
  },
  definedName: {
    title: 'Nombres definidos',
    add: 'A\u00f1adir nombre',
    name: 'Nombre',
    scope: '\u00c1mbito',
    comment: 'Comentario',
    refRange: 'Se refiere a',
    confirm: 'Aceptar',
    cancel: 'Cancelar',
    empty: 'Sin nombres definidos',
    nameRequired: 'El nombre es obligatorio',
    nameConflict: 'Ya existe un nombre igual',
    workbook: 'Libro',
    worksheet: 'Hoja actual',
  },

  // ---------------------------------------------------------------------
  // Men\u00fa contextual con comentarios, hipervv\u00ednculos, definiciones \u2014 Univer
  // a veces consulta estas keys adicionales.
  // ---------------------------------------------------------------------
  sheet: {
    numfmt: {
      decimalLength: 'Decimales',
      decimalPlaces: 'Lugares decimales',
      negType: 'N\u00fameros negativos',
      currencyType: 'Moneda',
      currencySymbol: 'S\u00edmbolo',
      dateFormat: 'Formato de fecha',
      accountingFormat: 'Formato contable',
      general: 'General',
      accounting: 'Contabilidad',
      text: 'Texto',
      number: 'N\u00famero',
      percent: 'Porcentaje',
      scientific: 'Cient\u00edfico',
      fraction: 'Fracci\u00f3n',
      currency: 'Moneda',
      date: 'Fecha',
      time: 'Hora',
      thousandsSeparator: 'Separador de miles',
      moreFmt: 'M\u00e1s formatos',
      customFormat: 'Formato personalizado',
      customFormatTitle: 'Formato personalizado',
    },
    cf: {
      title: 'Formato condicional',
      add: 'A\u00f1adir regla',
      edit: 'Editar regla',
      delete: 'Eliminar regla',
      manage: 'Gestionar reglas',
      applyRange: 'Aplicar a',
      type: 'Tipo',
      greaterThan: 'Mayor que',
      lessThan: 'Menor que',
      equal: 'Igual a',
      between: 'Entre',
      notBetween: 'No entre',
      textContains: 'El texto contiene',
      dateIs: 'La fecha es',
      duplicate: 'Valores duplicados',
      unique: 'Valores \u00fanicos',
      top10: '10 superiores',
      bottom10: '10 inferiores',
      aboveAverage: 'Por encima de la media',
      belowAverage: 'Por debajo de la media',
      confirm: 'Aceptar',
      cancel: 'Cancelar',
    },
  },
};

// ---------------------------------------------------------------------------
// Nombres por defecto de pestañas de hoja.
//
// Univer 0.4.2 (UMD en-US) **no trae traducciones individuales** para
// "sheet1", "sheet2", ... (solo `sheets.tabs.sheet` = "Sheet"). Cuando el
// usuario añade una hoja desde el botón "+" del sheet-bar, Univer construye
// el nombre concatenando `sheets.tabs.` + id interno (`sheet1`, `sheet2`...)
// y lo mete como **nombre real** del sheet en el workbook. Como nuestro dict
// tampoco cubría esas claves, los usuarios terminaban viendo literalmente
// "sheets.tabs.sheet2" como nombre de pestaña (y luego persistido al xlsx).
//
// Inyectamos entradas explícitas sheet1..sheet40 para cubrir los casos
// razonables. Si alguien crea >40 hojas verá la clave cruda, pero es un
// caso extremo.
// ---------------------------------------------------------------------------
if (!dict.sheets) dict.sheets = {};
if (!dict.sheets.tabs) dict.sheets.tabs = {};
// Univer 0.4.2 genera el nombre de una nueva hoja concatenando el template
// `sheets.tabs.sheet` + el índice. Con espacio final → "Hoja 2", "Hoja 3", ...
dict.sheets.tabs.sheet = 'Hoja ';
dict.sheets.tabs.sheetCopy = '(Copia{0})';
// Además cubrimos sheet1..sheet40 por si Univer los busca explícitamente
// (algunos code paths sí lo hacen, p.ej. cuando duplicas una hoja).
for (let i = 1; i <= 40; i++) {
  dict.sheets.tabs['sheet' + i] = 'Hoja ' + i;
}

export default dict;
